import { useState } from 'react';
import { Spinner } from '@belong/ui';
import { useResidentPreQualification } from './queries';
import { ResidentPreQualification } from './resident-pre-qualification';
import { RESIDENT_PRE_QUALIFICATION_STRINGS as STRINGS } from './resident-pre-qualification.strings';
import { isPreQualificationRequiredError } from './utils';

type TourIntent = 'BookTour' | 'RequestTour';

type GuardedAction = () => void | Promise<void>;

type PendingStep = {
  intent: TourIntent;
  action: GuardedAction;
  isRequiredByServer: boolean;
};

export function usePreQualificationGate({ homeId }: { homeId?: string }) {
  const { preQualification, isPreQualificationLoading, preQualificationError } =
    useResidentPreQualification({ homeId });
  const [pendingStep, setPendingStep] = useState<PendingStep | null>(null);

  const isSatisfied =
    !homeId ||
    !!preQualificationError ||
    (!!preQualification &&
      preQualification.estimatedOutcome !== 'NotQualified');

  function guard(intent: TourIntent, action: GuardedAction) {
    if (isSatisfied) {
      return action();
    }

    setPendingStep({ intent, action, isRequiredByServer: false });
  }

  function guardAfterServerError(
    intent: TourIntent,
    error: unknown,
    action: GuardedAction,
  ) {
    if (!homeId || !isPreQualificationRequiredError(error)) {
      return false;
    }

    setPendingStep({ intent, action, isRequiredByServer: true });

    return true;
  }

  function close() {
    setPendingStep(null);
  }

  return {
    guard,
    guardAfterServerError,
    isGateLoading: isPreQualificationLoading,
    content: !pendingStep ? null : isPreQualificationLoading ? (
      <div className="relative min-h-labeled-fields">
        <Spinner />
      </div>
    ) : (
      <ResidentPreQualification
        homeId={homeId!}
        intent={pendingStep.intent}
        ctaLabel={STRINGS[`cta.${pendingStep.intent}`]}
        initialPreQualification={
          pendingStep.isRequiredByServer ? undefined : preQualification
        }
        canProceedWhenUnavailable={!pendingStep.isRequiredByServer}
        onProceed={() => {
          close();
          Promise.resolve(pendingStep.action()).catch((error) =>
            console.error('Pre-qualified tour action failed', error),
          );
        }}
        onBack={close}
      />
    ),
  };
}
